import { useContext, useEffect } from 'react'
import { AppContext, AppContextType } from '../AppContext'
import { MediaSelection, UserContext, UserContextType } from '../UserContext'
import {
    media_selection_prev_preference,
    win32_muse_midi_output_device_label,
    win32_muse_midi_passthrough_device_label,
} from '../services/Constants'
import { env } from '../services/EnvironmentVariables'
import { persistent } from '../services/LocalStorage'
import { getMidiDevices } from '../services/MediaDevices'

export default function useWindowsMidiService() {
    const { state: appState } = useContext(AppContext) as AppContextType
    const { state, dispatch } = useContext(UserContext) as UserContextType

    useEffect(() => {
        if (env.OS !== 'win32') {
            return
        }

        ;(async () => {
            if (appState.appSettings.forceAccessMidiForWindows) {
                const { midiInputs, midiOutputs } = await getMidiDevices()
                dispatch({
                    type: 'update_media_sources',
                    value: {
                        midiInputs: midiInputs,
                        midiOutputs: midiOutputs,
                    },
                })
            }
        })()
    }, [appState.appSettings.forceAccessMidiForWindows])

    useEffect(() => {
        if (env.OS !== 'win32') {
            return
        }

        const delta: Partial<MediaSelection> = {}

        const prevPref = persistent.getObject(media_selection_prev_preference) as {
            midiOutputDevice: string | null
            midiOutputPassthroughDevice: string | null
        } | null
        // recall: midiOutputDevice and midiOutputPassthroughDevice stores the full name (label)
        // of the midi device.

        if (state.mediaSources.midiOutputs.length > 0) {
            // if mediaSelection for midiOutputs is empty, then find and set default.
            if (!state.mediaSelection.midiOutput || state.mediaSelection.midiOutput === '_undef') {
                const prevPrefDevice =
                    (prevPref?.midiOutputDevice &&
                        state.mediaSources.midiOutputs.find((a) => a.name === prevPref.midiOutputDevice)) ||
                    null
                const museMidiOutput = state.mediaSources.midiOutputs.find(
                    (a) => a.name === win32_muse_midi_output_device_label
                )
                if (prevPrefDevice) {
                    delta.midiOutput = prevPrefDevice.id
                } else if (museMidiOutput) {
                    delta.midiOutput = museMidiOutput.id
                }
            }

            // same for the passthrough output.
            if (
                !state.mediaSelection.midiPassthroughOutput ||
                state.mediaSelection.midiPassthroughOutput === '_undef'
            ) {
                const prevPrefDevice =
                    (prevPref?.midiOutputPassthroughDevice &&
                        state.mediaSources.midiOutputs.find((a) => a.name === prevPref.midiOutputPassthroughDevice)) ||
                    null
                const museMidiPassthrough = state.mediaSources.midiOutputs.find(
                    (a) => a.name === win32_muse_midi_passthrough_device_label
                )
                if (prevPrefDevice) {
                    delta.midiPassthroughOutput = prevPrefDevice.id
                } else if (museMidiPassthrough) {
                    delta.midiPassthroughOutput = museMidiPassthrough.id
                }
            }
        }

        if (Object.keys(delta).length > 0) {
            dispatch({
                type: 'update_media_selection',
                value: delta,
            })
        }
    }, [state.mediaSources.midiOutputs, state.mediaSelection.midiOutput, state.mediaSelection.midiPassthroughOutput])
}
